import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import Typography from "../components/ui/Typography";
import Button from "../components/ui/Button";
import Chip from "../components/ui/Chip";
import ErrorAlert from "../components/ErrorAlert";
import ArchiveDiscoverySearch from "../components/ArchiveDiscoverySearch";
import BrowsePageIntro from "../components/BrowsePageIntro";
import usePageMeta from "../hooks/usePageMeta";
import { buildArchiveSeoMeta } from "./archive/archiveSeo";
import { fetchMonthIndex } from "../services/backendApi";
import { handleInternalLinkClick } from "../utils/navigation";
import { cn } from "../utils/classNames";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const posterSliceWidthClass = (count) => {
  const visibleCount = Math.min(Math.max(count, 1), 4);
  if (visibleCount === 1) return "w-full";
  if (visibleCount === 2) return "w-1/2";
  if (visibleCount === 3) return "w-1/3";
  return "w-1/4";
};

const getNewestYearMonth = (items = []) =>
  items.reduce((latest, item) => {
    const value = typeof item?.yearMonth === "string" ? item.yearMonth : null;
    if (!value) return latest;
    if (!latest || value > latest) return value;
    return latest;
  }, null);

const shouldReplaceMonths = (currentMonths = [], fetchedMonths = []) => {
  if (!Array.isArray(fetchedMonths) || fetchedMonths.length === 0) {
    return currentMonths.length === 0;
  }
  if (!Array.isArray(currentMonths) || currentMonths.length === 0) {
    return true;
  }
  if (fetchedMonths.length > currentMonths.length) {
    return true;
  }
  const currentNewest = getNewestYearMonth(currentMonths);
  const fetchedNewest = getNewestYearMonth(fetchedMonths);
  return Boolean(
    fetchedNewest &&
    currentNewest &&
    fetchedNewest > currentNewest
  );
};

const ArchiveStructuredData = ({ months, totalShows }) => {
  if (!months || months.length === 0) return null;

  const collectionJson = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "TV Show Premiere Archive",
    description: `Search ${totalShows} scripted TV premieres across ${months.length} months by mood, title, platform or premiere month.`,
    url: "https://premierelens.com/archive",
    hasPart: months.map((month) => ({
      "@type": "WebPage",
      name: `New TV Shows Premiering ${month.label}`,
      url: `https://premierelens.com${month.href}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJson) }}
    />
  );
};

ArchiveStructuredData.propTypes = {
  months: PropTypes.arrayOf(PropTypes.object),
  totalShows: PropTypes.number,
};

const MonthCard = ({ month }) => {
  const [isHovered, setIsHovered] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const posters = month.posters || [];

  return (
    <motion.a
      href={month.href}
      onClick={(e) => handleInternalLinkClick(e, month.href)}
      className={cn(
        "group bg-card text-card-foreground border border-accent-gold/70 rounded-xl overflow-hidden",
        "relative transition-all duration-200 block"
      )}
      initial={{ y: 0, rotateX: 0, boxShadow: "var(--shadow-card-base)" }}
      animate={
        isHovered
          ? {
              y: prefersReducedMotion ? 0 : -6,
              rotateX: prefersReducedMotion ? 0 : 2,
              boxShadow: "var(--shadow-card-hover)",
            }
          : { y: 0, rotateX: 0, boxShadow: "var(--shadow-card-base)" }
      }
      transition={{
        duration: 0.25,
        ease: [0.0, 0.0, 0.2, 1],
        boxShadow: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1] },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Poster strip */}
      <div className="relative h-32 overflow-hidden border-b border-accent-gold/60 bg-muted/30">
        {posters.length > 0 ? (
          <div className="flex h-full">
            {posters.map((item, index) => (
              <div
                key={index}
                className={cn("relative flex-none overflow-hidden", posterSliceWidthClass(posters.length))}
              >
                <img
                  src={item.poster}
                  alt={item.title}
                  className="w-full h-full object-cover grayscale-[20%] contrast-110 transition-transform duration-200 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-black/30 mix-blend-multiply" />
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted/50">
            <Typography variant="body-small" className="text-muted-foreground">
              No posters available
            </Typography>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6">
        <div className="border-b border-dashed border-accent-gold/60 pb-3 mb-3">
          <Typography as="h3" variant="h4" className="text-foreground leading-tight">
            {month.label}
          </Typography>
        </div>
        <div className="flex items-center justify-between gap-3">
          <Chip variant="muted" size="badge" className="text-meta">
            {month.count} {month.count === 1 ? "premiere" : "premieres"}
          </Chip>
          <Typography variant="caption" className="text-primary">
            Open month
          </Typography>
        </div>
      </div>
    </motion.a>
  );
};

MonthCard.propTypes = {
  month: PropTypes.shape({
    yearMonth: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number,
    href: PropTypes.string.isRequired,
    posters: PropTypes.arrayOf(
      PropTypes.shape({
        poster: PropTypes.string,
        title: PropTypes.string,
      })
    ),
  }).isRequired,
};

const ArchivePage = ({ initialArchivePayload = null }) => {
  const [months, setMonths] = useState(() =>
    Array.isArray(initialArchivePayload?.months) ? initialArchivePayload.months : []
  );
  const [loading, setLoading] = useState(() => !initialArchivePayload?.months);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handlePopState = () => setSearch(window.location.search || "");
    handlePopState();
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        const hasInitialPayload = Boolean(initialArchivePayload?.months);
        if (!hasInitialPayload) {
          setLoading(true);
        }
        const payload = await fetchMonthIndex({ preferStatic: hasInitialPayload });
        if (!active) return;
        const fetchedMonths = Array.isArray(payload?.months) ? payload.months : [];
        setMonths((currentMonths) => (
          shouldReplaceMonths(currentMonths, fetchedMonths) ? fetchedMonths : currentMonths
        ));
        setError("");
      } catch (nextError) {
        if (!active) return;
        if (!initialArchivePayload?.months) {
          setError(nextError.message || "Failed to load archive");
        }
      } finally {
        if (active && !initialArchivePayload?.months) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [initialArchivePayload, loadAttempt]);

  const hasSearchQuery = useMemo(() => {
    const params = new URLSearchParams(search);
    return Boolean(params.get("q") || params.get("tag") || params.get("tags"));
  }, [search]);

  usePageMeta(buildArchiveSeoMeta({ noindex: hasSearchQuery }));

  const totalShows = months.reduce((sum, m) => sum + (m.count || 0), 0);
  const oldestMonth = months.reduce((oldest, m) => (!oldest || m.yearMonth < oldest.yearMonth ? m : oldest), null);
  const newestMonth = months.reduce((newest, m) => (!newest || m.yearMonth > newest.yearMonth ? m : newest), null);
  const busiestMonth = months.reduce((busiest, m) => (!busiest || (m.count || 0) > (busiest.count || 0) ? m : busiest), null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader
        backHref="/"
        backLabel="Home"
      />
      <ArchiveStructuredData months={months} totalShows={totalShows} />

      <main id="main-content" tabIndex={-1} className="route-shell route-shell--browse">
        <BrowsePageIntro
          id="archive-page-title"
          variant="archive"
          title="Find a show in the archive"
          description="Describe what you want to watch, search by title, platform or month, or name a show you like and see more of its feel."
          actions={(
            <Button variant="outline" asChild>
              <a
                href="/most-recommended"
                onClick={(event) => handleInternalLinkClick(event, "/most-recommended")}
              >
                Get TV recommendations
              </a>
            </Button>
          )}
        >
          {months.length > 0 && (
            <ul className="flex flex-wrap gap-x-6 gap-y-1 text-body-small text-muted-foreground">
              <li><span className="font-semibold text-foreground">{totalShows}</span> premieres</li>
              <li>
                <span className="font-semibold text-foreground">{months.length}</span> months, {oldestMonth.label} to {newestMonth.label}
              </li>
              <li>
                Busiest: <span className="font-semibold text-foreground">{busiestMonth.label}</span> ({busiestMonth.count})
              </li>
            </ul>
          )}
        </BrowsePageIntro>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-label="Loading archive months">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="aspect-[4/3] animate-pulse rounded-lg border border-border/70 bg-card/60 p-4">
                <div className="h-2/3 rounded-md bg-muted" />
                <div className="mt-4 h-5 w-2/3 rounded bg-muted" />
                <div className="mt-2 h-4 w-1/3 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="mx-auto max-w-2xl space-y-4 text-center">
            <ErrorAlert message="The archive could not be loaded right now." />
            <Button type="button" variant="outline" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
              Try again
            </Button>
          </div>
        ) : months.length === 0 ? (
          <div className="text-center py-16">
            <Typography variant="body" className="text-muted-foreground">
              No completed archive months available yet.
            </Typography>
          </div>
        ) : (
          <ArchiveDiscoverySearch search={search}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {months.map((month) => {
                const bestOfHref = `/best-of/${month.yearMonth}`;
                return (
                  <div key={month.yearMonth} className="grid gap-2">
                    <MonthCard month={month} />
                    <a
                      href={bestOfHref}
                      onClick={(event) => handleInternalLinkClick(event, bestOfHref)}
                      className="inline-flex min-h-11 w-fit items-center gap-1.5 text-body-small font-semibold text-primary underline-offset-4 hover:underline"
                    >
                      <Star className="h-4 w-4" aria-hidden="true" />
                      Best of {month.label}
                    </a>
                  </div>
                );
              })}
            </div>
          </ArchiveDiscoverySearch>
        )}
      </main>

      <SiteFooter />
    </div>
  );
};

ArchivePage.propTypes = {
  initialArchivePayload: PropTypes.shape({
    months: PropTypes.arrayOf(
      PropTypes.shape({
        yearMonth: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        count: PropTypes.number,
        href: PropTypes.string.isRequired,
        posters: PropTypes.arrayOf(
          PropTypes.shape({
            poster: PropTypes.string,
            title: PropTypes.string,
          })
        ),
      })
    ),
  }),
};

export default ArchivePage;
